import { workflowJsep } from '../../../../lib/formula/jsepInstances';
import { WorkflowNodeFilterDataType } from '../../../../ee/lib/workflow/node/ifTypes';
import { escapeVariableKey, unescapeVariableKey } from '../../../../ee/lib/workflow/variable-generator';
/**
 * Maps VariableType to WorkflowNodeFilterDataType
 */
function variableTypeToFilterDataType(variable) {
    // Any type with isArray should be treated as MULTI_SELECT (e.g. MultiSelect is string + isArray)
    if (variable.isArray) {
        return WorkflowNodeFilterDataType.MULTI_SELECT;
    }
    switch (variable.type) {
        case 'string':
            return WorkflowNodeFilterDataType.TEXT;
        case 'number':
        case 'integer':
            return WorkflowNodeFilterDataType.NUMBER;
        case 'boolean':
            return WorkflowNodeFilterDataType.BOOLEAN;
        case 'date':
            return WorkflowNodeFilterDataType.DATE;
        case 'datetime':
            return WorkflowNodeFilterDataType.DATETIME;
        case 'object':
            // Check if it's an object with children (nested structure)
            if (variable.children && variable.children.length > 0) {
                // For structured objects with children, don't auto-select type
                return undefined;
            }
            // Default to text for simple objects
            return WorkflowNodeFilterDataType.TEXT;
        case 'array':
            return WorkflowNodeFilterDataType.MULTI_SELECT;
        default:
            return WorkflowNodeFilterDataType.TEXT;
    }
}
/**
 * Recursively searches for a variable in the children hierarchy
 */
function searchInChildren(path, variables) {
    for (const variable of variables) {
        // Check direct match
        if (variable.key === path) {
            return variable;
        }
        // Search in children recursively
        if (variable.children && variable.children.length > 0) {
            const found = searchInChildren(path, variable.children);
            if (found) {
                return found;
            }
        }
    }
    return undefined;
}
/**
 * Finds a variable by path in the flatVariables list
 * Handles nested paths like "$('Node').trigger.timestamp"
 * If the exact path is not found, searches in children of parent variables
 */
function findVariable(path, flatVariables) {
    // First, try direct match in top-level variables
    const directMatch = flatVariables.find((v) => v.key === path);
    if (directMatch)
        return directMatch;
    // Search recursively through all children
    const foundInChildren = searchInChildren(path, flatVariables);
    if (foundInChildren)
        return foundInChildren;
    // Fallback: try partial matches
    return flatVariables.find((v) => {
        if (v.key.endsWith(`.${path}`))
            return true;
        if (v.key.startsWith(path))
            return true;
        return false;
    });
}
/**
 * Gets the return type of built-in workflow functions
 * Based on WorkflowExpressionParser in nocodb-sdk
 */
function getBuiltInFunctionReturnType(functionName) {
    // Type checking functions return boolean
    if (['$isNull', '$isUndefined', '$isEmpty'].includes(functionName)) {
        return WorkflowNodeFilterDataType.BOOLEAN;
    }
    // Type conversion functions
    if (functionName === '$string')
        return WorkflowNodeFilterDataType.TEXT;
    if (functionName === '$number')
        return WorkflowNodeFilterDataType.NUMBER;
    if (functionName === '$boolean')
        return WorkflowNodeFilterDataType.BOOLEAN;
    // Array/String length returns number
    if (functionName === '$length')
        return WorkflowNodeFilterDataType.NUMBER;
    // Math functions return number
    if (['$abs', '$ceil', '$floor', '$round', '$min', '$max'].includes(functionName)) {
        return WorkflowNodeFilterDataType.NUMBER;
    }
    // $first and $last return element type (default to TEXT)
    if (['$first', '$last'].includes(functionName)) {
        return WorkflowNodeFilterDataType.TEXT;
    }
    // $ifEmpty depends on second argument, default to TEXT
    if (functionName === '$ifEmpty') {
        return WorkflowNodeFilterDataType.TEXT;
    }
    return undefined;
}
/**
 * Gets the return type of object methods (string, array, number)
 * Based on allowed methods in WorkflowExpressionParser
 */
function getMethodReturnType(methodName) {
    // String methods returning string
    const stringMethods = [
        'toUpperCase',
        'toLowerCase',
        'trim',
        'slice',
        'substring',
        'charAt',
        'concat',
        'replace',
        'repeat',
        'padStart',
        'padEnd',
    ];
    if (stringMethods.includes(methodName)) {
        return WorkflowNodeFilterDataType.TEXT;
    }
    // String methods returning number
    const stringNumberMethods = ['indexOf', 'lastIndexOf', 'charCodeAt'];
    if (stringNumberMethods.includes(methodName)) {
        return WorkflowNodeFilterDataType.NUMBER;
    }
    // String methods returning boolean
    const stringBooleanMethods = ['startsWith', 'endsWith', 'includes'];
    if (stringBooleanMethods.includes(methodName)) {
        return WorkflowNodeFilterDataType.BOOLEAN;
    }
    // String split returns array
    if (methodName === 'split') {
        return WorkflowNodeFilterDataType.MULTI_SELECT;
    }
    // Array methods returning string
    if (methodName === 'join') {
        return WorkflowNodeFilterDataType.TEXT;
    }
    // Array methods returning number
    const arrayNumberMethods = ['indexOf', 'lastIndexOf', 'findIndex'];
    if (arrayNumberMethods.includes(methodName)) {
        return WorkflowNodeFilterDataType.NUMBER;
    }
    // Array methods returning boolean
    const arrayBooleanMethods = ['some', 'every', 'includes'];
    if (arrayBooleanMethods.includes(methodName)) {
        return WorkflowNodeFilterDataType.BOOLEAN;
    }
    // Array methods returning array
    const arrayArrayMethods = ['map', 'filter', 'slice', 'concat', 'flat'];
    if (arrayArrayMethods.includes(methodName)) {
        return WorkflowNodeFilterDataType.MULTI_SELECT;
    }
    // Number methods returning string
    const numberStringMethods = ['toFixed', 'toExponential', 'toPrecision'];
    if (numberStringMethods.includes(methodName)) {
        return WorkflowNodeFilterDataType.TEXT;
    }
    return undefined;
}
function getMemberExpressionReturnType(propertyName) {
    switch (propertyName) {
        case 'length':
            return WorkflowNodeFilterDataType.NUMBER;
        default:
            return undefined;
    }
}
/**
 * Parses a workflow variable reference from a MemberExpression
 * e.g., $('Node').trigger.timestamp
 */
function parseWorkflowVariable(node, flatVariables) {
    // Handle MemberExpression: $('Node').trigger.timestamp or $('Node')['field name']
    if (node.type === 'MemberExpression') {
        // Build the path from the member expression, tracking whether each segment uses bracket notation
        const pathSegments = [];
        let current = node;
        while (current.type === 'MemberExpression') {
            // Handle both dot notation (Identifier) and bracket notation (Literal)
            if (current.property.type === 'Identifier') {
                pathSegments.unshift({
                    value: current.property.name,
                    useBracket: false,
                });
            }
            else if (current.property.type === 'Literal') {
                // Bracket notation: ['field name']
                pathSegments.unshift({
                    value: String(current.property.value),
                    useBracket: true,
                });
            }
            current = current.object;
        }
        // current should now be a CallExpression: $('Node')
        // Extract the node name and build the full variable path
        let nodeName = '';
        if (current.type === 'CallExpression' &&
            current.callee.type === 'Identifier' &&
            current.callee.name === '$') {
            if (current.arguments.length > 0 &&
                current.arguments[0].type === 'Literal') {
                nodeName = current.arguments[0].value;
            }
        }
        // Build the full path with node name, preserving bracket notation
        let fullPath = '';
        if (nodeName) {
            fullPath = `$('${nodeName}')`;
            pathSegments.forEach((seg) => {
                if (seg.useBracket) {
                    fullPath += `['${escapeVariableKey(seg.value)}']`;
                }
                else {
                    fullPath += `.${seg.value}`;
                }
            });
        }
        else {
            fullPath = pathSegments
                .map((seg, i) => {
                if (seg.useBracket) {
                    return `['${escapeVariableKey(seg.value)}']`;
                }
                else {
                    return i === 0 ? seg.value : `.${seg.value}`;
                }
            })
                .join('');
        }
        const variable = findVariable(fullPath, flatVariables);
        if (variable) {
            const dataType = variableTypeToFilterDataType(variable);
            return {
                type: 'Identifier',
                dataType,
                name: variable.key,
            };
        }
    }
    // Handle simple CallExpression: $('variable')
    if (node.type === 'CallExpression' &&
        node.callee.type === 'Identifier' &&
        node.callee.name === '$') {
        if (node.arguments.length > 0 && node.arguments[0].type === 'Literal') {
            const nodeName = node.arguments[0].value;
            const fullPath = `$('${nodeName}')`;
            const variable = findVariable(fullPath, flatVariables);
            if (variable) {
                const dataType = variableTypeToFilterDataType(variable);
                return {
                    type: 'Identifier',
                    dataType,
                    name: variable.key,
                };
            }
        }
    }
    return null;
}
function validateAndExtractType(node, flatVariables) {
    const result = Object.assign({}, node);
    switch (node.type) {
        case 'Literal':
            if (typeof node.value === 'number') {
                result.dataType = WorkflowNodeFilterDataType.NUMBER;
            }
            else if (typeof node.value === 'string') {
                result.dataType = WorkflowNodeFilterDataType.TEXT;
            }
            else if (typeof node.value === 'boolean') {
                result.dataType = WorkflowNodeFilterDataType.BOOLEAN;
            }
            break;
        case 'Identifier':
            result.dataType = WorkflowNodeFilterDataType.TEXT;
            break;
        case 'CallExpression': {
            // Check if it's a workflow variable reference
            const varNode = parseWorkflowVariable(node, flatVariables);
            if (varNode) {
                return varNode;
            }
            // Check if it's a built-in function (like $abs, $isNull, etc.)
            if (node.callee.type === 'Identifier' &&
                node.callee.name.startsWith('$')) {
                const functionType = getBuiltInFunctionReturnType(node.callee.name);
                if (functionType) {
                    result.dataType = functionType;
                    break;
                }
            }
            // Check if it's a method call (like .toUpperCase(), .indexOf(), etc.)
            if (node.callee.type === 'MemberExpression' &&
                node.callee.property.type === 'Identifier') {
                const methodType = getMethodReturnType(node.callee.property.name);
                if (methodType) {
                    result.dataType = methodType;
                    break;
                }
            }
            // Otherwise, default to TEXT
            result.dataType = WorkflowNodeFilterDataType.TEXT;
            break;
        }
        case 'MemberExpression': {
            // Check if this is a method/property access (not a call)
            if (node.property.type === 'Identifier') {
                // Check if it's a known property like 'length'
                const propertyType = getMemberExpressionReturnType(node.property.name);
                if (propertyType) {
                    result.dataType = propertyType;
                    break;
                }
            }
            // This might be a workflow variable reference
            const memberVar = parseWorkflowVariable(node, flatVariables);
            if (memberVar) {
                return memberVar;
            }
            result.dataType = WorkflowNodeFilterDataType.TEXT;
            break;
        }
        case 'UnaryExpression': {
            const argument = validateAndExtractType(node.argument, flatVariables);
            // Handle negative numbers and logical NOT
            if (node.operator === '-') {
                result.dataType = WorkflowNodeFilterDataType.NUMBER;
            }
            else if (node.operator === '!') {
                result.dataType = WorkflowNodeFilterDataType.BOOLEAN;
            }
            result.argument = argument;
            break;
        }
        case 'BinaryExpression':
            {
                const left = validateAndExtractType(node.left, flatVariables);
                const right = validateAndExtractType(node.right, flatVariables);
                // Apply type inference rules based on operator
                if (['==', '===', '!=', '!==', '<', '>', '<=', '>='].includes(node.operator)) {
                    // Comparison operators return boolean
                    result.dataType = WorkflowNodeFilterDataType.BOOLEAN;
                }
                else if (node.operator === '+') {
                    // In workflow expressions, + is ONLY for numeric addition
                    // String concatenation uses .concat() method
                    result.dataType = WorkflowNodeFilterDataType.NUMBER;
                }
                else if (node.operator === '&') {
                    // String concatenation operator
                    result.dataType = WorkflowNodeFilterDataType.TEXT;
                }
                else if (['-', '*', '/', '%', '**'].includes(node.operator)) {
                    // Arithmetic operations return number
                    result.dataType = WorkflowNodeFilterDataType.NUMBER;
                }
                else {
                    result.dataType = WorkflowNodeFilterDataType.TEXT;
                }
                result.left = left;
                result.right = right;
            }
            break;
        case 'LogicalExpression':
            {
                // Logical operators (&&, ||) return boolean
                const logicalLeft = validateAndExtractType(node.left, flatVariables);
                const logicalRight = validateAndExtractType(node.right, flatVariables);
                result.dataType = WorkflowNodeFilterDataType.BOOLEAN;
                result.left = logicalLeft;
                result.right = logicalRight;
            }
            break;
        case 'ConditionalExpression': {
            // Ternary operator: condition ? consequent : alternate
            const test = validateAndExtractType(node.test, flatVariables);
            const consequent = validateAndExtractType(node.consequent, flatVariables);
            const alternate = validateAndExtractType(node.alternate, flatVariables);
            // Try to infer type from consequent/alternate branches
            // If both have same type, use that; otherwise default to TEXT
            if (consequent.dataType && consequent.dataType === alternate.dataType) {
                result.dataType = consequent.dataType;
            }
            else {
                // we default to TEXT as a fallback. This is a conservative choice to avoid incorrect type assumptions.
                result.dataType = WorkflowNodeFilterDataType.TEXT;
            }
            result.test = test;
            result.consequent = consequent;
            result.alternate = alternate;
            break;
        }
        default:
            result.dataType = WorkflowNodeFilterDataType.TEXT;
    }
    return result;
}
/**
 * Detects the WorkflowNodeFilterDataType from a workflow field expression
 * Handles expressions like:
 * - {{ $('Node').trigger.timestamp }} -> DATETIME
 * - {{ $('Node').trigger.timestamp + 100 }} -> NUMBER (+ is numeric only)
 * - {{ $('Node').user.email.toUpperCase() }} -> TEXT
 * - {{ $abs(-5) }} -> NUMBER
 * - {{ $isNull($('Node').value) }} -> BOOLEAN
 */
export function extractDataTypeFromWorkflowNodeExpression(fieldExpression, flatVariables) {
    if (!fieldExpression)
        return undefined;
    // Plain text (no expression) — detect literal type
    if (!fieldExpression.includes('{{')) {
        const trimmed = fieldExpression.trim();
        if (trimmed === 'true' || trimmed === 'false') {
            return WorkflowNodeFilterDataType.BOOLEAN;
        }
        if (trimmed !== '' && !isNaN(Number(trimmed))) {
            return WorkflowNodeFilterDataType.NUMBER;
        }
        return WorkflowNodeFilterDataType.TEXT;
    }
    try {
        // Check if the field is ONLY an expression (no surrounding text)
        // Only detect type for pure expressions like "{{ ... }}" with optional whitespace
        const trimmed = fieldExpression.trim();
        // Count expression delimiters - should be exactly one {{ and one }}
        const openCount = (fieldExpression.match(/\{\{/g) || []).length;
        const closeCount = (fieldExpression.match(/}}/g) || []).length;
        // Multiple expressions like "{{ expr1 }} {{ expr2 }}" should be TEXT
        if (openCount !== 1 || closeCount !== 1) {
            return WorkflowNodeFilterDataType.TEXT;
        }
        const isPureExpression = /^\{\{\s*[^}]+\s*}}$/.test(trimmed);
        // If there's text mixed with the expression, treat as TEXT
        if (!isPureExpression) {
            return WorkflowNodeFilterDataType.TEXT;
        }
        // Extract expression from {{ }}
        const match = fieldExpression.match(/\{\{\s*([^}]+?)\s*}}/);
        if (!match || !match[1])
            return WorkflowNodeFilterDataType.TEXT;
        const expression = match[1];
        const parsed = workflowJsep(expression);
        const result = validateAndExtractType(parsed, flatVariables);
        return result.dataType;
    }
    catch (error) {
        console.warn('Failed to parse workflow expression:', error);
        return WorkflowNodeFilterDataType.TEXT;
    }
}
/**
 * Finds the variable definition for a workflow expression
 * @param expression - The expression string (e.g., "{{ $('Node').records }}")
 * @param flatVariables - Array of available variable definitions
 * @returns The variable definition or null if not found
 */
export function findVariableForExpression(expression, flatVariables) {
    if (!expression || !expression.includes('{{'))
        return null;
    try {
        const trimmed = expression.trim();
        const match = trimmed.match(/\{\{\s*([^}]+?)\s*}}/);
        if (!match || !match[1])
            return null;
        const expr = match[1];
        const parsed = workflowJsep(expr);
        // Try to extract variable from the parsed expression
        const varNode = parseWorkflowVariable(parsed, flatVariables);
        if (varNode && varNode.name) {
            return findVariable(varNode.name, flatVariables) || null;
        }
        return null;
    }
    catch (_a) {
        return null;
    }
}
/**
 * Extracts node reference and variable path from a workflow expression
 * Handles simple variable references like:
 * - {{ $('NodeName').path.to.variable }}
 * - {{ $('NodeName')['path'] }}
 * - {{ $('NodeName')['path']['to']['variable'] }}
 *
 * @param expression - The expression string (e.g., "{{ $('Node').records }}")
 * @returns Parsed variable info or null if not a simple variable reference
 *
 * @example
 * parseWorkflowVariableExpression("{{ $('Get Records').records }}")
 * // Returns: { nodeTitle: 'Get Records', variablePath: 'records' }
 *
 * @example
 * parseWorkflowVariableExpression("{{ $('Get Records')['records'] }}")
 * // Returns: { nodeTitle: 'Get Records', variablePath: 'records' }
 */
export function parseWorkflowVariableExpression(expression) {
    if (!expression)
        return null;
    // Remove {{ }} if present
    const cleanExpression = expression.replace(/^\{\{\s*|\s*}}$/g, '').trim();
    // Match pattern: $('NodeName')
    const nodeMatch = cleanExpression.match(/^\$\(['"]([^'"]+)['"]\)/);
    if (!nodeMatch)
        return null;
    const nodeTitle = nodeMatch[1];
    const remainder = cleanExpression.substring(nodeMatch[0].length);
    // If there's no path after the node reference
    if (!remainder) {
        return {
            nodeTitle,
            variablePath: '',
        };
    }
    // Extract the path - can be dot notation or bracket notation
    // Examples: .records, ['records'], .records.field, ['records']['field']
    const pathParts = [];
    let currentPos = 0;
    while (currentPos < remainder.length) {
        // Match dot notation: .propertyName
        const dotMatch = remainder.substring(currentPos).match(/^\.([a-zA-Z_$][a-zA-Z0-9_$]*)/);
        if (dotMatch) {
            pathParts.push(dotMatch[1]);
            currentPos += dotMatch[0].length;
            continue;
        }
        // Match bracket notation: ['propertyName'] or ["propertyName"] (with escaped quotes)
        const bracketMatch = remainder.substring(currentPos).match(/^\['((?:[^'\\]|\\.)*)'\]/) ||
            remainder.substring(currentPos).match(/^\["((?:[^"\\]|\\.)*)"\]/);
        if (bracketMatch) {
            pathParts.push(unescapeVariableKey(bracketMatch[1]));
            currentPos += bracketMatch[0].length;
            continue;
        }
        // If we can't match anything, this might be a complex expression
        return null;
    }
    // Join path parts with dots for consistency
    const variablePath = pathParts.join('.');
    return {
        nodeTitle,
        variablePath,
    };
}
//# sourceMappingURL=data:application/json;base64,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